import React from 'react'
import ReactDOM from 'react-dom';

import {select, put, takeEvery, takeLeading} from 'redux-saga/effects';
import * as actionTypes from '../actions/profile/actionTypes';
import * as actions from '../actions/';
import axios from '../../services/axios-client';

//Components
import Notification from '../../components/Notifications/Notifications';

//Utils
import qs from 'qs';
import i18n from '../../translations/i18n';
import camelize from 'camelize';

export function* getProfile() {
    try {
        const companyAPIKey = yield select(state => state.auth.companyKey);
        const userAccessToken = yield select(state => state.auth.userToken);
        const locale = yield select(state => state.locales.locale);

        const resp = yield axios.request({
            method: 'POST',
            url: '/api/client/profile/get',
            headers: {
                'company-api-key': companyAPIKey,
                'user-access-token': userAccessToken,
                'locale': locale
            }
        });

        const {data: {result: payload}} = resp;
        yield put(actions.getProfileSucceeded(payload));
    } catch (err) {
        if (err.response && err.response.status === 401) {
            yield put(actions.logout());
            return;
        }

        ReactDOM.render(
            <Notification type='error' error={err}/>,
            document.getElementById('notification')
        );
    }
}

export function* updateProfile({
    profile: {
        phoneNumber,
        firstName,
        lastName,
        birthDate,
        address,
        zipCode,
        city,
        companyName,
        extraFieldValue,
    },
}) {

    try {
        const companyAPIKey = yield select(state => state.auth.companyKey);
        const userAccessToken = yield select(state => state.auth.userToken);
        const locale = yield select(state => state.locales.locale);
        yield axios.request({
            method: 'POST',
            url: '/api/client/profile/update',
            data: qs.stringify({
                contact_number: phoneNumber,
                first_name: firstName,
                last_name: lastName,
                birth_date: birthDate,
                address,
                zip_code: zipCode,
                city,
                client_company_name: companyName,
                external_note: extraFieldValue,
            }),
            headers: {
                'company-api-key': companyAPIKey,
                'user-access-token': userAccessToken,
                'locale': locale
            }
        });

        yield put(actions.updateProfileSucceeded({
            phoneNumber,
            firstName,
            lastName,
            birthDate,
            address,
            zipCode,
            city,
            companyName,
            extraFieldValue,
        }));
        yield put(actions.updateProfileFailed({}));

        ReactDOM.render(
            <Notification type='success' message={i18n.t('common:myProfile.editAccount.accountSavedSuccessfully')}/>,
            document.getElementById('notification')
        );
    } catch (err) {
        if (err.response && err.response.status === 401) {
            yield put(actions.logout());
            return
        }
        if (err.response && err.response.data && err.response.data.result && err.response.data.result.errors) {
            const {errors} = err.response.data.result;
            yield put(actions.updateProfileFailed(errors));
            return
        }
        ReactDOM.render(
            <Notification type='error' error={err}/>,
            document.getElementById('notification')
        );
    }
}

export function* updateSettings({settings: {locale, pushEnabled, mailEnabled, feedbackEnabled}}) {
    try {
        yield put(actions.startLoading());
        const companyAPIKey = yield select(state => state.auth.companyKey);
        const userAccessToken = yield select(state => state.auth.userToken);

        yield axios.request({
            method: 'POST',
            url: '/api/client/settings/update',
            data: qs.stringify({
                locale,
                push_enabled: Number(pushEnabled),
                email_enabled: Number(mailEnabled),
                feedback_enabled: Number(feedbackEnabled)
            }),
            headers: {
                'company-api-key': companyAPIKey,
                'user-access-token': userAccessToken,
                'locale': locale
            }
        });

        yield put(actions.updateSettingsSucceeded({
            pushEnabled,
            mailEnabled,
            feedbackEnabled
        }));
        yield put(actions.changeLocale(locale));


        ReactDOM.render(
            <Notification type='success' message={i18n.t('common:myProfile.settings.settingsSavedSuccessfully')}/>,
            document.getElementById('notification')
        );
        yield put(actions.stopLoading());
    } catch (err) {
        yield put(actions.stopLoading());
        if (err.response && err.response.status === 401) {
            yield put(actions.logout());
            return
        }
        ReactDOM.render(
            <Notification type='error' error={err}/>,
            document.getElementById('notification')
        );
    }
}

export function* updateAvatar({avatar}) {
    try {
        const companyAPIKey = yield select(state => state.auth.companyKey);
        const userAccessToken = yield select(state => state.auth.userToken);
        const locale = yield select(state => state.locales.locale);

        const data = new FormData();
        data.append('file', avatar);

        const resp = yield axios.request({
            method: 'POST',
            url: '/api/client/avatar/update',
            data,
            headers: {
                'company-api-key': companyAPIKey,
                'user-access-token': userAccessToken,
                'locale': locale,
                'Content-Type': 'multipart/form-data'
            }
        });

        const {data: {result}} = resp;
        if (!result) {
            return;
        }

        const {avatar_url: newAvatar} = result;
        yield put(actions.updateAvatarSucceeded(newAvatar));
        ReactDOM.render(
            <Notification type='success' message={i18n.t('common:myProfile.avatarChanged')}/>,
            document.getElementById('notification')
        );
    } catch (error) {
        if (error.response && error.response.status === 401) {
            yield put(actions.logout());
            return
        }
        ReactDOM.render(
            <Notification type='error' error={error}/>,
            document.getElementById('notification')
        );
    }
}

export function* changePassword({currentPassword, newPassword}) {
    try {
        yield put(actions.startLoading());
        const companyAPIKey = yield select(state => state.auth.companyKey);
        const userAccessToken = yield select(state => state.auth.userToken);
        const locale = yield select(state => state.locales.locale);

        yield axios.request({
            method: 'POST',
            url: '/api/client/password/change',
            data: qs.stringify({
                current_password: currentPassword,
                new_password: newPassword
            }),
            headers: {
                'company-api-key': companyAPIKey,
                'user-access-token': userAccessToken,
                'locale': locale
            }
        });
        ReactDOM.render(
            <Notification type='success'
                          message={i18n.t('common:myProfile.editPassword.passwordChangedSuccessfully')}/>,
            document.getElementById('notification')
        );
        yield put(actions.changePasswordFailed({}));
        yield put(actions.stopLoading());
    } catch (error) {
        yield put(actions.stopLoading());
        if (error.response && error.response.status === 401) {
            yield put(actions.logout());
        }
        if (error.response && error.response.data && error.response.data.result && error.response.data.result.errors) {
            const {errors} = error.response.data.result;
            yield put(actions.changePasswordFailed(camelize(errors)));
            return
        }
        ReactDOM.render(
            <Notification type='error' error={error}/>,
            document.getElementById('notification')
        );
    }
}

export const watchProfile = [
    takeEvery(actionTypes.GET_PROFILE, getProfile),
    takeLeading(actionTypes.UPDATE_PROFILE, updateProfile),
    takeLeading(actionTypes.UPDATE_SETTINGS, updateSettings),
    takeEvery(actionTypes.UPDATE_AVATAR, updateAvatar),
    takeLeading(actionTypes.CHANGE_PASSWORD, changePassword)
];
